import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LoanWithRenewalData } from '../../../loan/domain/models/loan.model';
import {
  AppBarTitleService,
  EnumHelper,
  AppBarActionsService,
  AppBarAction,
  SubtransactionData,
  TransactionType,
  TransactionStatus,
  SubtransactionType,
  AccountTransaction,
  SubtransactionTypeConsumerLabel,
  TransactionStatusLabel,
  FailureReasonBaseData,
  SelectorPipe,
  SimpleDialogService,
  CommonActionKey,
  HorizontalListData,
  SimpleHorizontalListWidgetConfigData,
} from 'common';
import * as _ from 'lodash';
import { AccountTransactionService } from '../account-transaction.service';
import { FailureReasonService } from '../failure-reason.service';
import { combineLatest } from 'rxjs';
import { AccountTransactionDetailAdapter } from './account-transaction-detail.data';

@UntilDestroy()
@Component({
  selector: 'ifp-account-transaction-detail',
  templateUrl: './account-transaction-detail.component.html',
  styleUrls: ['./account-transaction-detail.component.scss'],
  providers: [AccountTransactionDetailAdapter],
  standalone: false,
})
export class AccountTransactionDetailComponent implements OnInit {
  displayedColumns: string[] = ['index', 'type', 'amount'];
  transaction: AccountTransaction;
  subtransactions: SubtransactionData[];
  loan: LoanWithRenewalData;
  form: UntypedFormGroup;
  listData: HorizontalListData[];
  listWidgetConfig: SimpleHorizontalListWidgetConfigData;

  readonly TransactionType = TransactionType;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly appBarTitleService: AppBarTitleService,
    private readonly selectorPipe: SelectorPipe,
    private readonly failureReasonService: FailureReasonService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly accountTransactionService: AccountTransactionService,
    private readonly simpleDialogService: SimpleDialogService,
    private readonly adapter: AccountTransactionDetailAdapter,
  ) {
    this.formInit();
  }

  ngOnInit(): void {
    combineLatest([this.route.data, this.failureReasonService.list()])
      .pipe(untilDestroyed(this))
      .subscribe(this.dataInit.bind(this));
  }

  formInit(): void {
    this.form = this.formBuilder.group({
      loanNumber: { value: undefined, disabled: true },
      businessName: { value: undefined, disabled: true },
      status: { value: undefined, disabled: true },
      failureReasons: { value: undefined, disabled: true },
    });
  }

  private dataInit([data, failureReasons]: [any, FailureReasonBaseData[]]): void {
    if (!data) return;

    this.loan = data.loan;
    this.transaction = data.transaction;
    this.subtransactions = _.filter(data.transaction.subtransactions, { display: true });
    this.listData = this.adapter.adaptListData(this.transaction, this.loan);
    this.listWidgetConfig = this.adapter.adaptWidgetConfigData();

    this.form.patchValue({
      loanNumber: this.transaction.loanNumber,
      businessName: this.transaction.businessName,
      status: this.getTransactionStatusLabel(this.transaction.status),
      failureReasons: this.getFailureReasons(this.transaction, failureReasons),
    });

    this.appBarTitleService.title = _.startCase(EnumHelper.getNameFromValue(TransactionType, this.transaction.type));
    this.appBarActionsService.actions = [
      {
        id: 'cancelTransaction',
        label: 'Cancel Transaction',
        buttonType: 'button',
        disabled: !this.transaction.cancelable,
      },
    ];

    this.appBarActionsService.invoking.pipe(untilDestroyed(this)).subscribe(this.actionDispatch.bind(this));
  }

  private getFailureReasons(transaction: AccountTransaction, failureReasons: FailureReasonBaseData[]): unknown {
    const filterFunc = (failureReason: FailureReasonBaseData, failureReasonCodes: string[]): boolean =>
      failureReasonCodes.includes(failureReason.code);
    const selectFunc = (failureReasons: FailureReasonBaseData[]): string =>
      failureReasons?.map((fr) => fr.name).join(', ') ?? '';

    return this.selectorPipe.transform(transaction.failureReasons, failureReasons, filterFunc, selectFunc);
  }

  getTypeName(type: SubtransactionType): string {
    return SubtransactionTypeConsumerLabel.get(type);
  }

  getTransactionStatusLabel(status?: TransactionStatus): string {
    if (status === TransactionStatus.OnHold) {
      status = TransactionStatus.Pending;
    }

    return EnumHelper.getMappedNameFromValue(TransactionStatus, TransactionStatusLabel, status);
  }

  actionDispatch(action: AppBarAction): void {
    const actionHandler: (action: AppBarAction) => void = this[action.id].bind(this);
    actionHandler(action);
  }

  cancelTransaction(): void {
    this.simpleDialogService
      .openSimpleConfirmDialog('Are you sure you want to cancel this transaction?', 'Cancel Transaction?')
      .subscribe((result: CommonActionKey) => {
        if (result === CommonActionKey.Confirm) {
          this.accountTransactionService.cancelTransaction(this.transaction.id).subscribe((e) => {
            location.reload();
          });
        }
      });
  }
}
